import { create } from "zustand";
import { dayjs } from "@boo/shared";
import type {
  BudgetData,
  BudgetStatusData,
  BudgetTrendData,
  CategoryBudgetItem,
  ExpenseCategory,
  MonthComparisonData,
  ProjectionsData,
} from "@boo/shared";
import {
  fetchBudget,
  fetchBudgetComparison,
  fetchBudgetProjections,
  fetchBudgetStatus,
  fetchBudgetTrend,
  saveBudget,
} from "../../../services/budgetApi";

interface BudgetStoreState {
  year: number;
  month: number;
  budget: BudgetData | null;
  budgetStatus: BudgetStatusData | null;
  projections: ProjectionsData | null;
  template: BudgetData | null;
  templateDismissed: boolean;
  loading: boolean;
  statusLoading: boolean;
  saving: boolean;
  error: string | null;
  pendingAddCategory: ExpenseCategory | null;
  trend: BudgetTrendData | null;
  trendLoading: boolean;
  comparison: MonthComparisonData | null;
  comparisonLoading: boolean;
  loadBudget: (year: number, month: number) => Promise<void>;
  loadBudgetStatus: (year: number, month: number) => Promise<void>;
  loadProjections: (year: number, month: number) => Promise<void>;
  loadTemplate: (year: number, month: number) => Promise<void>;
  loadTrend: (months: number, category?: ExpenseCategory) => Promise<void>;
  loadComparison: (year: number, month: number) => Promise<void>;
  upsertBudget: (overallAmount: number) => Promise<void>;
  upsertCategoryBudgets: (
    categoryBudgets: CategoryBudgetItem[],
  ) => Promise<void>;
  applyTemplate: () => Promise<void>;
  dismissTemplate: () => void;
  setPendingAddCategory: (category: ExpenseCategory | null) => void;
}

export const useBudgetStore = create<BudgetStoreState>()((set, get) => ({
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  budget: null,
  budgetStatus: null,
  projections: null,
  template: null,
  templateDismissed: false,
  loading: false,
  statusLoading: false,
  saving: false,
  error: null,
  pendingAddCategory: null,
  trend: null,
  trendLoading: false,
  comparison: null,
  comparisonLoading: false,

  async loadBudgetStatus(year, month) {
    set({ statusLoading: true });
    try {
      const budgetStatus = await fetchBudgetStatus(year, month);
      set({ budgetStatus });
    } catch {
      // status view degrades gracefully — no error shown
    } finally {
      set({ statusLoading: false });
    }
  },

  async loadBudget(year, month) {
    set({ loading: true, error: null, year, month, templateDismissed: false });
    try {
      const budget = await fetchBudget(year, month);
      set({ budget });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Failed to load budget",
      });
    } finally {
      set({ loading: false });
    }
  },

  async loadProjections(year, month) {
    try {
      const projections = await fetchBudgetProjections(year, month);
      set({ projections });
    } catch {
      set({ projections: null });
    }
  },

  async loadTemplate(year, month) {
    const prev = dayjs(new Date(year, month - 1, 1)).subtract(1, "month");
    try {
      const template = await fetchBudget(prev.year(), prev.month() + 1);
      set({ template });
    } catch {
      set({ template: null });
    }
  },

  async upsertBudget(overallAmount) {
    const { year, month, budget } = get();
    set({ saving: true, error: null });
    try {
      const updated = await saveBudget(
        year,
        month,
        overallAmount,
        budget?.categoryBudgets,
      );
      set({ budget: updated });
      get().loadBudgetStatus(year, month);
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Failed to save budget",
      });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  async upsertCategoryBudgets(categoryBudgets) {
    const { year, month, budget } = get();
    const overallAmount = budget?.overallAmount ?? 0;
    set({ saving: true, error: null });
    try {
      const updated = await saveBudget(
        year,
        month,
        overallAmount,
        categoryBudgets,
      );
      set({ budget: updated });
      get().loadBudgetStatus(year, month);
    } catch (err) {
      set({
        error:
          err instanceof Error
            ? err.message
            : "Failed to save category budgets",
      });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  async applyTemplate() {
    const { year, month, template } = get();
    if (!template) return;
    set({ saving: true, error: null });
    try {
      const updated = await saveBudget(
        year,
        month,
        template.overallAmount,
        template.categoryBudgets,
      );
      set({ budget: updated, templateDismissed: true });
      get().loadBudgetStatus(year, month);
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Failed to apply template",
      });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  async loadTrend(months, category) {
    set({ trendLoading: true });
    try {
      const trend = await fetchBudgetTrend(months, category);
      set({ trend });
    } catch {
      set({ trend: null });
    } finally {
      set({ trendLoading: false });
    }
  },

  async loadComparison(year, month) {
    set({ comparisonLoading: true });
    try {
      const comparison = await fetchBudgetComparison(year, month);
      set({ comparison });
    } catch {
      set({ comparison: null });
    } finally {
      set({ comparisonLoading: false });
    }
  },

  dismissTemplate() {
    set({ templateDismissed: true });
  },

  setPendingAddCategory(category) {
    set({ pendingAddCategory: category });
  },
}));
